import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { RotateCcw, ArrowDownLeft, ArrowUpRight, X } from 'lucide-react'
import { api } from '../lib/api'
import { Card, CardContent } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { useActiveTenant } from '../contexts/tenant'
import { useAuth } from '../contexts/auth'

type Move = {
  id: string; movedAt: string; movementType: string; quantity: number
  notes: string | null; referenceType: string | null
  sku: string | null; productName: string | null; locationCode: string | null; author: string | null
}

const TYPE_LABEL: Record<string, string> = {
  inbound: 'Entrada', outbound: 'Saída', transfer_in: 'Transf. entra', transfer_out: 'Transf. sai',
  adjustment: 'Ajuste', return: 'Devolução',
}

export function MovimentacoesPage() {
  const tenantId = useActiveTenant()
  const { user } = useAuth()
  const qc = useQueryClient()
  const isAdmin = user?.role === 'admin'
  const [reversing, setReversing] = useState<string | null>(null)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [bulkBusy, setBulkBusy] = useState(false)
  const [params, setParams] = useSearchParams()
  const productId = params.get('productId') ?? undefined
  const productLabel = params.get('sku') ?? undefined

  const { data: moves = [], isLoading } = useQuery<Move[]>({
    queryKey: ['movements', tenantId, productId],
    queryFn: async () => (await api.get('/stock/movements', { params: { tenantId, productId, limit: 300 } })).data,
    enabled: !!tenantId,
  })

  // Busca por SKU → resolve o productId (pra ver o histórico + saldo por localização).
  const { data: products = [] } = useQuery<{ id: string; sku: string; name: string }[]>({
    queryKey: ['products', tenantId],
    queryFn: async () => (await api.get('/products', { params: { tenantId } })).data,
    enabled: !!tenantId,
  })
  const { data: balance = [] } = useQuery<{ locationCode: string | null; quantity: number }[]>({
    queryKey: ['balance-by-product', tenantId, productId],
    queryFn: async () => (await api.get('/stock/balance', { params: { tenantId, productId } })).data,
    enabled: !!tenantId && !!productId,
  })
  const [skuInput, setSkuInput] = useState('')
  function buscarSku(e: React.FormEvent) {
    e.preventDefault()
    const q = skuInput.trim().toLowerCase()
    if (!q) { setParams({}); return }
    const p = products.find(x => x.sku.toLowerCase() === q) ?? products.find(x => x.sku.toLowerCase().includes(q))
    if (p) setParams({ productId: p.id, sku: p.sku })
    else alert(`SKU "${skuInput}" não encontrado nos produtos deste cliente.`)
  }

  async function estornar(m: Move) {
    if (!window.confirm(`Estornar este movimento? Será lançado o oposto (${m.quantity > 0 ? '' : '+'}${-m.quantity} un de ${m.sku ?? ''}) para corrigir o saldo.`)) return
    setReversing(m.id)
    try {
      await api.post(`/stock/movements/${m.id}/reverse`)
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['movements', tenantId] }),
        qc.invalidateQueries({ queryKey: ['stock', 'balance', tenantId] }),
        qc.invalidateQueries({ queryKey: ['stock', 'consolidated', tenantId] }),
      ])
    } catch (e: any) {
      alert(e?.response?.data?.title ?? 'Falha ao estornar')
    } finally {
      setReversing(null)
    }
  }

  const canReverse = (m: Move) => !(m.movementType === 'adjustment' && m.notes?.startsWith('Estorno'))
  const reversible = moves.filter(canReverse)
  const allSelected = reversible.length > 0 && reversible.every(m => selected.has(m.id))
  function toggle(id: string) {
    setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n })
  }
  function toggleAll() {
    setSelected(allSelected ? new Set() : new Set(reversible.map(m => m.id)))
  }

  async function estornarSelecionados() {
    const ids = [...selected]
    if (!ids.length) return
    if (!window.confirm(`Estornar ${ids.length} movimento(s) selecionado(s)? Será lançado o oposto de cada um.`)) return
    setBulkBusy(true)
    let ok = 0, ja = 0, err = 0
    for (const id of ids) {
      try { await api.post(`/stock/movements/${id}/reverse`); ok++ }
      catch (e: any) { if (e?.response?.status === 409) ja++; else err++ }
    }
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['movements', tenantId] }),
      qc.invalidateQueries({ queryKey: ['stock', 'balance', tenantId] }),
      qc.invalidateQueries({ queryKey: ['stock', 'consolidated', tenantId] }),
    ])
    setSelected(new Set())
    setBulkBusy(false)
    alert(`Estornados: ${ok}${ja ? ` · já estornados: ${ja}` : ''}${err ? ` · erros: ${err}` : ''}.`)
  }

  if (!tenantId) {
    return <div className="flex h-64 items-center justify-center text-sm text-gray-500">Selecione um cliente no menu lateral para continuar.</div>
  }

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Movimentações</h2>
        <p className="text-sm text-gray-500">Histórico de tudo que entrou e saiu, por SKU e local — de onde veio e quem fez (registro que não se apaga). {isAdmin && 'Dá para estornar um movimento errado.'}</p>
        {productId && (
          <button onClick={() => { setSkuInput(''); setParams({}) }} className="mt-2 inline-flex items-center gap-1 rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100">
            Filtrando: {productLabel ?? 'produto'} <X className="h-3 w-3" />
          </button>
        )}
      </div>

      <form onSubmit={buscarSku} className="flex flex-wrap items-center gap-2">
        <input
          value={skuInput}
          onChange={e => setSkuInput(e.target.value)}
          placeholder="Buscar por SKU (ex.: 9) — vê saldo por local + histórico"
          className="w-72 rounded-lg border border-gray-200 px-3 py-2 text-sm"
        />
        <Button size="sm" type="submit">Ver</Button>
      </form>

      {productId && (
        <div className="rounded-lg border border-gray-200 bg-white p-3">
          <p className="mb-2 text-xs font-medium text-gray-500">Saldo por localização de <b>{productLabel ?? 'produto'}</b> — o abastecer usa o saldo do <b>fracionamento/GERAL</b>: se ele já cobre a demanda, o SKU não é pedido.</p>
          {balance.length === 0 ? (
            <p className="text-sm text-amber-700">Sem saldo em nenhuma localização — o fracionamento está <b>zerado</b>. Se havia demanda, o abastecer deveria ter pedido este SKU.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {balance.map((b, i) => (
                <span key={i} className={`rounded-lg border px-3 py-1.5 text-sm ${b.quantity < 0 ? 'border-red-200 bg-red-50 text-red-700' : 'border-gray-200 bg-gray-50 text-gray-700'}`}>
                  <b>{b.locationCode ?? 'GERAL'}</b>: {b.quantity}{b.quantity < 0 ? ' ⚠️' : ''}
                </span>
              ))}
              <span className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-semibold text-gray-900">
                Total: {balance.reduce((s, b) => s + b.quantity, 0)}
              </span>
            </div>
          )}
        </div>
      )}

      {isAdmin && selected.size > 0 && (
        <div className="flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5">
          <span className="text-sm font-medium text-amber-900">{selected.size} selecionado(s)</span>
          <Button size="sm" variant="danger" loading={bulkBusy} onClick={estornarSelecionados}>
            <RotateCcw className="h-4 w-4" /> Estornar selecionados
          </Button>
          <button className="text-xs text-gray-500 hover:text-gray-700" onClick={() => setSelected(new Set())}>limpar</button>
        </div>
      )}

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <p className="px-5 py-10 text-center text-sm text-gray-400">Carregando...</p>
          ) : moves.length === 0 ? (
            <p className="px-5 py-10 text-center text-sm text-gray-400">Nenhuma movimentação registrada.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-gray-100 bg-gray-50">
                  <tr>
                    {isAdmin && <th className="px-3 py-3"><input type="checkbox" checked={allSelected} onChange={toggleAll} title="Selecionar todos" /></th>}
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Quando</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">SKU</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Produto</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Tipo</th>
                    <th className="px-4 py-3 text-right font-medium text-gray-600">Qtd</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Local</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">De onde / Obs.</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Quem</th>
                    {isAdmin && <th className="px-4 py-3" />}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {moves.map(m => (
                    <tr key={m.id} className={`hover:bg-gray-50 ${selected.has(m.id) ? 'bg-amber-50/50' : ''}`}>
                      {isAdmin && (
                        <td className="px-3 py-2.5">
                          {canReverse(m) && <input type="checkbox" checked={selected.has(m.id)} onChange={() => toggle(m.id)} />}
                        </td>
                      )}
                      <td className="px-4 py-2.5 whitespace-nowrap text-gray-500">{new Date(m.movedAt).toLocaleString('pt-BR')}</td>
                      <td className="px-4 py-2.5 font-mono font-medium text-gray-900">{m.sku ?? '—'}</td>
                      <td className="px-4 py-2.5 text-gray-700">{m.productName ?? '—'}</td>
                      <td className="px-4 py-2.5 text-gray-600">{TYPE_LABEL[m.movementType] ?? m.movementType}</td>
                      <td className={`px-4 py-2.5 text-right font-semibold ${m.quantity < 0 ? 'text-red-600' : 'text-green-600'}`}>
                        <span className="inline-flex items-center gap-1">
                          {m.quantity < 0 ? <ArrowUpRight className="h-3.5 w-3.5" /> : <ArrowDownLeft className="h-3.5 w-3.5" />}
                          {m.quantity > 0 ? '+' : ''}{m.quantity}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 font-mono text-gray-500">{m.locationCode ?? '—'}</td>
                      <td className="px-4 py-2.5 text-gray-500">{m.notes ?? '—'}</td>
                      <td className="px-4 py-2.5 text-gray-500">{m.author ?? '—'}</td>
                      {isAdmin && (
                        <td className="px-4 py-2.5 text-right">
                          {m.movementType !== 'adjustment' || !m.notes?.startsWith('Estorno') ? (
                            <Button variant="ghost" size="sm" title="Estornar (lança o oposto)"
                              loading={reversing === m.id} onClick={() => estornar(m)}>
                              <RotateCcw className="h-4 w-4 text-gray-500" />
                            </Button>
                          ) : null}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <p className="text-xs text-gray-400">
        "Estornar" cria um movimento oposto (não apaga o original) — o histórico continua íntegro e o saldo é corrigido.
        Para a baixa em dobro, estorne a linha "Saída · Separação …" que sobrou.
      </p>
    </div>
  )
}
